import { Ionicons } from "@/src/components/ui/ionicons";
import { Image } from "expo-image";
import * as WebBrowser from "expo-web-browser";
import React, { useCallback } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import { Text } from "./ui/BrandText";
import { Alert } from "../lib/alert";
import { isSafeSponsoredUrl } from "../lib/sponsoredFeed";
import { action, border, radius, spacing, surface, text, typography } from "../theme/tokens";
import type { SponsoredFeedItem } from "../types/sponsored";

type Props = {
  item: SponsoredFeedItem;
  onPressCta: (item: SponsoredFeedItem) => void;
};

function SponsoredPostCardBase({ item, onPressCta }: Props) {
  const hasCta = !!item.ctaLabel && isSafeSponsoredUrl(item.ctaUrl);

  const handleCta = useCallback(() => {
    if (!hasCta || !item.ctaUrl) return;
    onPressCta(item);
    void WebBrowser.openBrowserAsync(item.ctaUrl).catch(() => {
      Alert.alert("Ouverture impossible", "Le lien sponsorisé n'a pas pu être ouvert.");
    });
  }, [hasCta, item, onPressCta]);

  return (
    <View style={styles.card} accessibilityLabel={`Contenu sponsorisé par ${item.advertiserName}`}>
      <View style={styles.content}>
        <View style={styles.sponsoredRow}>
          <View style={styles.labelPill}>
            <Ionicons name="megaphone-outline" size={14} color={text.brand} />
            <Text style={styles.sponsoredLabel}>Sponsorisé</Text>
          </View>
          <Text style={styles.advertiser}>{item.advertiserName}</Text>
        </View>

        {item.headline ? <Text style={styles.headline}>{item.headline}</Text> : null}
        <Text style={styles.body}>{item.body}</Text>

        {item.mediaUrl ? (
          <Image
            source={{ uri: item.mediaUrl }}
            cachePolicy="disk"
            contentFit="cover"
            transition={150}
            accessibilityLabel={`Média sponsorisé de ${item.advertiserName}`}
            style={styles.media}
          />
        ) : null}

        {hasCta ? (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel={`${item.ctaLabel}, lien sponsorisé externe`}
            onPress={handleCta}
            style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
          >
            <Text style={styles.ctaLabel}>{item.ctaLabel}</Text>
            <Ionicons name="open-outline" size={17} color={text.inverse} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: surface.primary,
    borderColor: border.brand,
    borderRadius: radius.xl,
    borderWidth: 1,
    overflow: "hidden",
  },
  content: { gap: spacing.md, padding: spacing.lg },
  sponsoredRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  labelPill: {
    alignItems: "center",
    backgroundColor: surface.selected,
    borderColor: border.brand,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexDirection: "row",
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  sponsoredLabel: {
    color: text.brand,
    fontSize: typography.size.caption,
    fontWeight: "800",
  },
  advertiser: { color: text.primary, fontSize: 14, fontWeight: "800" },
  headline: {
    color: text.primary,
    fontSize: typography.size.title,
    fontWeight: "800",
    lineHeight: typography.lineHeight.title,
  },
  body: {
    color: text.primary,
    fontSize: 15,
    lineHeight: 22,
  },
  media: {
    aspectRatio: 16 / 9,
    backgroundColor: surface.elevated,
    borderRadius: radius.lg,
    width: "100%",
  },
  cta: {
    alignItems: "center",
    alignSelf: "flex-start",
    backgroundColor: action.primary,
    borderRadius: radius.md,
    cursor: Platform.OS === "web" ? "pointer" : "auto",
    flexDirection: "row",
    gap: spacing.sm,
    justifyContent: "center",
    minHeight: 48,
    minWidth: 120,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  ctaPressed: { backgroundColor: action.primaryPressed },
  ctaLabel: { color: text.inverse, fontWeight: "800" },
});

export const SponsoredPostCard = React.memo(SponsoredPostCardBase);
